import { GlobalGameStore } from '@game-libs/global-game-store';
import { CONFIG } from '../config';
import type { GUIGameState } from '../types/game-types';
import { loadState } from './save-state';
import { buildStatsSnapshot, updateStats } from '../stats';
import { unitStats } from '../stats';
import { inspirationReducer } from './reducers/generator-reducers/inspiration.reducer';
import { INITIAL_CURRENCY_STATE, useCurrencyStore } from './zustand/useCurrencyStore';
import { useDragonHealthStore } from './zustand/useDragonHealthStore';
import { useDungeonCrawlerStore } from './zustand/useDungeonCrawlerStore';
import { useGameStatusStore, type GameStatusState } from './zustand/useGameStatusStore';
import { useGameStatsStore } from './zustand/useGameStatsStore';
import { useTimedEventsStore } from './zustand/useTimedEventsStore';
import { applyLegacyGeneratorsFromMainSaveIfStoreStillDefault, useGeneratorsStore } from './zustand/useGeneratorsStore';
import { applyLegacyUpgradesFromMainSaveIfStoreStillDefault, useUpgradesStore } from './zustand/useUpgradesStore';
import { TimedEventsOrchestrator } from '@game-libs/timed-events-orchestrator';
import { sceneNameToGameLevelId } from '../types/game-levels';
import { TEST_INITIAL_VALUES } from '../../e2e/utils/constants';

/** Wait for Zustand persist so getState() matches storage (avoids racing async rehydration). */
async function awaitPersistRehydration(): Promise<void> {
  await Promise.all([
    useCurrencyStore.persist.rehydrate(),
    useDragonHealthStore.persist.rehydrate(),
    useDungeonCrawlerStore.persist.rehydrate(),
    useGameStatusStore.persist.rehydrate(),
    useGameStatsStore.persist.rehydrate(),
    useTimedEventsStore.persist.rehydrate(),
    useGeneratorsStore.persist.rehydrate(),
    useUpgradesStore.persist.rehydrate(),
  ]);
}

/**
 * After the small-dragon “coup de grâce”, MET_DRAGON can advance health to the big dragon while
 * game-status flags sometimes fail to persist before refresh (split Zustand keys). Detect that and
 * the intentional “dad dragon” phase, or 0 HP small dragon (including after WIN_GAME saved won), then rewind to
 * the last stable tutorial checkpoint: 1 HP small dragon, tutorial dungeon index, scene name tutorial.
 */
function shouldRewindTutorialBossCheckpoint(
  gameStatus: Pick<GameStatusState, 'dragonName' | 'metBigDragon'>,
  dragonHealth: number,
): boolean {
  if (gameStatus.dragonName === 'dummy') {
    return false;
  }

  const smallDragonMaxHealth = CONFIG.game.initialDragonHealth.smallDragon;

  if (gameStatus.dragonName === 'bigDragon') {
    return true;
  }

  if (gameStatus.dragonName === 'smallDragon') {
    if (gameStatus.metBigDragon) {
      return true;
    }
    if (dragonHealth > smallDragonMaxHealth) {
      return true;
    }
    if (dragonHealth <= 0) {
      return true;
    }
  }

  return false;
}

/** Applies the rewind by mutating Zustand stores (dragon/dungeon no longer live on GUIGameState). */
function rewindTutorialBossCheckpointIfStale(): void {
  const gameStatus = useGameStatusStore.getState();
  const dragonHealth = useDragonHealthStore.getState().dragonHealth;

  if (!shouldRewindTutorialBossCheckpoint(gameStatus, dragonHealth)) {
    return;
  }

  GlobalGameStore().actions.setCurrentLevel('tutorial');

  useGameStatusStore.getState().sync({
    dragonName: 'smallDragon',
    metBigDragon: false,
    playedCoupDeGrace: true,
    stunEffectTickDurationRemaining: 0,
    won: false,
  });

  useDragonHealthStore.getState().sync({
    dragonHealth: 1,
    remainingPoison: 0,
    damageLastTick: 0,
  });

  useDungeonCrawlerStore.getState().sync({
    currentLevel: 0,
    isOpen: false,
  });
}

/**
 * Playwright baseline: fast ticks always; currency seed only when no per-level play stats exist yet
 * (ticks/clicks/birds all zero in `useGameStatsStore`). URL params use the same gate so `e2e_gold=…`
 * does not clobber Zustand-persisted gold after a mid-game reload.
 */
function applyWebdriverBaseline(): void {
  if (useGameStatsStore.getState().hasNoPlayStatsAnyLevel()) {
    useCurrencyStore.getState().sync({
      gold: TEST_INITIAL_VALUES.GOLD,
      wood: INITIAL_CURRENCY_STATE.wood,
      ore: INITIAL_CURRENCY_STATE.ore,
      food: INITIAL_CURRENCY_STATE.food,
      mana: INITIAL_CURRENCY_STATE.mana,
      goldLastTick: INITIAL_CURRENCY_STATE.goldLastTick,
      inspiration: { ...INITIAL_CURRENCY_STATE.inspiration },
    });
  }
  // Fast ticks for Playwright (tick length lives in useGameStatusStore).
  useGameStatusStore.getState().sync({ tickLength: TEST_INITIAL_VALUES.TICK_LENGTH });
}

export const initReducerState = async () => {
  await awaitPersistRehydration();

  const [loadedState, secondsOffline, versionMismatch, legacyGeneratorsFromMainBlob, legacyUpgradesFromMainBlob] =
    await loadState();

  // Never resume with the dungeon modal "open" from persistence (matches old main-blob load behavior).
  useDungeonCrawlerStore.getState().sync({ isOpen: false });

  const gameState = {
    ...loadedState,
  };

  let newState: GUIGameState = { ...gameState };

  {
    const currentSceneName = GlobalGameStore().state.currentLevel.value;
    const sceneForStatsBucket =
      currentSceneName && currentSceneName.length > 0 ? currentSceneName : 'tutorial';
    useGameStatsStore.getState().setActiveLevelId(sceneNameToGameLevelId(sceneForStatsBucket));
  }

  if (versionMismatch) {
    useDragonHealthStore.getState().reset();
    useGameStatusStore.getState().reset();
    useGameStatsStore.getState().reset();
    useTimedEventsStore.getState().reset();
    TimedEventsOrchestrator().clearEvents();
    useCurrencyStore.getState().reset();
    useDungeonCrawlerStore.getState().reset();
    useGeneratorsStore.getState().reset();
    useUpgradesStore.getState().reset();

    // Scene + progress globals live in GlobalGameStore; reset in memory so main.ts starts tutorial.
    const { actions: globalActions, state: globalState } = GlobalGameStore();
    globalActions.setCurrentLevel('tutorial');
    globalState.globalVariables.value = {};
    globalActions.saveState();

    useGameStatsStore.getState().setActiveLevelId(sceneNameToGameLevelId('tutorial'));
  }

  applyLegacyGeneratorsFromMainSaveIfStoreStillDefault(legacyGeneratorsFromMainBlob);
  applyLegacyUpgradesFromMainSaveIfStoreStillDefault(legacyUpgradesFromMainBlob);

  const isWebdriver = typeof window !== 'undefined' && window.navigator.webdriver === true;

  if (!isWebdriver) {
    rewindTutorialBossCheckpointIfStale();
  }

  if (isWebdriver) {
    applyWebdriverBaseline();
  }

  if (isWebdriver) {
    const params = new URLSearchParams(window.location.search);
    // Same rule as currency baseline: only seed from URL when there is no per-level play progress yet.
    const useE2eUrlSeeds = useGameStatsStore.getState().hasNoPlayStatsAnyLevel();

    const e2eDragonHealth = params.get('e2e_dragon_health');
    if (useE2eUrlSeeds && e2eDragonHealth !== null) {
      const health = parseInt(e2eDragonHealth, 10);
      if (!isNaN(health) && health >= 0) {
        useDragonHealthStore.getState().setHealth(health);
      }
    }

    const e2eGold = params.get('e2e_gold');
    if (useE2eUrlSeeds && e2eGold !== null) {
      const gold = parseInt(e2eGold, 10);
      if (!isNaN(gold) && gold >= 0) {
        useCurrencyStore.getState().setGold(gold);
      }
    }

    const e2eMana = params.get('e2e_mana');
    if (useE2eUrlSeeds && e2eMana !== null) {
      const mana = parseInt(e2eMana, 10);
      if (!isNaN(mana) && mana >= 0) {
        useCurrencyStore.getState().setMana(mana);
      }
    }

    const e2eWizardOwned = params.get('e2e_wizard_owned');
    if (useE2eUrlSeeds && e2eWizardOwned !== null) {
      const owned = parseInt(e2eWizardOwned, 10);
      if (!isNaN(owned) && owned >= 0) {
        const generatorsSnapshot = useGeneratorsStore.getState().generators;
        useGeneratorsStore.getState().setGenerators({
          ...generatorsSnapshot,
          wizard: {
            ...generatorsSnapshot.wizard,
            owned,
            purchased: owned,
            isUnlocked: owned > 0,
          },
        });
      }
    }

    const e2eBardOwned = params.get('e2e_bard_owned');
    if (useE2eUrlSeeds && e2eBardOwned !== null) {
      const owned = parseInt(e2eBardOwned, 10);
      if (!isNaN(owned) && owned >= 0) {
        const generatorsSnapshot = useGeneratorsStore.getState().generators;
        useGeneratorsStore.getState().setGenerators({
          ...generatorsSnapshot,
          bard: {
            ...generatorsSnapshot.bard,
            owned,
            purchased: owned,
            isUnlocked: owned > 0,
          },
        });
      }
    }

    const e2eInspirationAmount = params.get('e2e_inspiration_amount');
    if (useE2eUrlSeeds && e2eInspirationAmount !== null) {
      const amount = parseInt(e2eInspirationAmount, 10);
      if (!isNaN(amount) && amount >= 0) {
        const inspiration = { ...useCurrencyStore.getState().inspiration, amount };
        useCurrencyStore.getState().sync({ inspiration });
      }
    }

    const e2eInspirationMultiplier = params.get('e2e_inspiration_multiplier');
    if (useE2eUrlSeeds && e2eInspirationMultiplier !== null) {
      const multiplier = parseFloat(e2eInspirationMultiplier);
      if (!isNaN(multiplier) && multiplier >= 1) {
        const inspiration = { ...useCurrencyStore.getState().inspiration, multiplier };
        useCurrencyStore.getState().sync({ inspiration });
      }
    }

    const e2eDungeonKeys = params.get('e2e_dungeon_keys');
    if (useE2eUrlSeeds && e2eDungeonKeys !== null) {
      const keys = parseInt(e2eDungeonKeys, 10);
      if (!isNaN(keys) && keys >= 0) {
        useDungeonCrawlerStore.getState().sync({ keys });
      }
    }

    const e2eDungeonLevel = params.get('e2e_dungeon_level');
    if (useE2eUrlSeeds && e2eDungeonLevel !== null) {
      const currentLevel = parseInt(e2eDungeonLevel, 10);
      if (!isNaN(currentLevel) && currentLevel >= 0) {
        useDungeonCrawlerStore.getState().sync({ currentLevel });
      }
    }
  }

  if (secondsOffline > 0 && useUpgradesStore.getState().upgrades.bard?.lockerRoomSpeech) {
    const lockerRoomSpeech = useUpgradesStore.getState().upgrades.bard.lockerRoomSpeech;
    const proportion = lockerRoomSpeech.multiplier;

    if (proportion > 0) {
      const currencySnapshot = useCurrencyStore.getState();
      if (currencySnapshot.inspiration.remainingActive > 0 || currencySnapshot.inspiration.remainingCooldown > 0) {
        useCurrencyStore.getState().sync({
          inspiration: {
            ...currencySnapshot.inspiration,
            remainingActive: 0,
            remainingCooldown: 0,
            multiplier: 1,
          },
        });
      }

      updateStats(buildStatsSnapshot());

      const tickLengthSeconds = useGameStatusStore.getState().tickLength / 1000;
      const ticksOffline = Math.floor(secondsOffline / tickLengthSeconds);

      const inspirationPerTick = unitStats.bard.inspirationGeneration || 0;
      const totalInspirationGenerated = inspirationPerTick * ticksOffline;
      const offlineInspiration = totalInspirationGenerated * (proportion / 100);

      if (offlineInspiration > 0) {
        const inspirationAfterOffline = inspirationReducer(
          useCurrencyStore.getState().inspiration,
          offlineInspiration,
          useUpgradesStore.getState().upgrades,
        );
        useCurrencyStore.getState().sync({ inspiration: inspirationAfterOffline });
      }
    }
  }

  return newState;
};

/** Runs initReducerState once; shared by main.ts (before game.start) and GameStateProvider. */
let bootstrapPromise: Promise<GUIGameState> | null = null;

export function ensureGameStateBootstrapped(): Promise<GUIGameState> {
  bootstrapPromise ??= initReducerState();
  return bootstrapPromise;
}
